import { OrderPlanningRowAction, ReasonMap } from "./OrderPlanningRowAction";
import { PlanningTableRowActionProps } from "@mcleod/general/src/planning/PlanningTableRowAction";
import { Button, Snackbar, TableRow } from "@mcleod/components";
import { ActionPermission, Api, ModelRow } from "@mcleod/core";
import { UnassignEquipmentDialog } from "../../UnassignEquipmentDialog";
import { RowDispatchControlFields } from "@mcleod/general/src/models/autogen/AutogenModelDispatchControl";
import { OrderMultiSelectErrorModal } from "@mcleod/general/src/OrderMultiSelectErrorModal";

export class RowActionUnassign extends OrderPlanningRowAction {
    override getButton(): Button {
        return this.toolPanel.buttonUnassign;
    }

    override getButtonId(): string {
        return "buttonUnassign";
    }

    override getFieldsUsedInThisFile(): string[] {
        return ["tractor", "driver1_id", "driver1_name", "driver2_id", "driver2_name", "order_id",
            "movement_id", "manifest_id", "linked_id"];
    }

    override getMaxSelections(): keyof RowDispatchControlFields {
        return "max_a_unassign_selections";
    }

    override getMultiSelectPermission(): ActionPermission {
        return ActionPermission.ACTION_MULTISELECT_UNASSIGN;
    }

    override actionValidateSelection(selectedRows: ModelRow[], reasonMap: ReasonMap): void {
        selectedRows.forEach(row => this.rowValidator(reasonMap, row));
    }

    private rowValidator(reasonMap: ReasonMap, modelRow: ModelRow) {
        const arrayField = "order_id";
        const hasTractor = !modelRow.isNull("tractor");
        const hasDriver = !modelRow.isNull("driver1_id") || !modelRow.isNull("driver2_id");

        if (hasTractor || hasDriver) {
            return;
        } else {
            this.addReason(reasonMap, "assigned", "No tractor or driver to unassign for orders [{0}].", modelRow, arrayField);
        }
    }

    override async execute(props: PlanningTableRowActionProps): Promise<void> {
        const isMultiSelect = props.selectedRows.length > 1;
        const selectedRows = props.selectedRows;
        const selectedRow = props.selectedRows[0]?.data as ModelRow;
        const driverName = selectedRow.isNull("driver1_name") ? selectedRow.getString("driver2_name") : selectedRow.getString("driver1_name");
        const tractorId = selectedRow.getString("tractor");
        const isLinkedMove = selectedRows.some(row => !row.data.isNull("linked_id"));
        const resultRow = await new UnassignEquipmentDialog({ codeType: "B", driverName, tractorId, isMultiSelect, isLinkedMove }).show();
        if (resultRow) {
            await this.unassignSelectedEquipment(selectedRows, resultRow);
        }
    }

    private async unassignSelectedEquipment(selectedRows: TableRow[], unassignReason: any, showResults: boolean = true): Promise<void> {
        const hasLinkedId = selectedRows.some(row => !row.data.isNull("linked_id"));
        const message = hasLinkedId ? "Unassigning equipment" : `Unassigning equipment for ${selectedRows.length} movement(s)...`;
        const inProgressSnackbar = this.showInProgressSnackbar(message);
        try {
            // Track linked_ids that have already been added to keys
            const processedLinkedIds = new Set<string>();
            const processedLinkedMovementIds = new Set<string>();
            const movementKeys = [];
            const linkedMovementData = [];

            selectedRows.forEach(row => {
                const movementId = row.data.getString("movement_id", null);
                const linkedId = row.data.get("linked_id", null);

                if (movementId) {
                    // Only add movement to keys if it's the first with this linked_id (or has no linked_id)
                    if (!linkedId || !processedLinkedIds.has(linkedId)) {
                        movementKeys.push({ movement_id: movementId });
                        if (linkedId) {
                            processedLinkedIds.add(linkedId);
                        }
                    }

                    // Add movement data only if there is a linked_id and it's the first one with this linked_id
                    if (linkedId && !processedLinkedMovementIds.has(linkedId)) {
                        linkedMovementData.push({
                            movement_id: movementId,
                            linked_id: linkedId
                        });
                        processedLinkedMovementIds.add(linkedId);
                    }
                }
            });

            const unassignStatusList = await Api.post("lme/dispatch/asset/unassign-equipment/batch", {
                "keys": movementKeys,
                "data": {
                    "unassign_reason": unassignReason instanceof ModelRow ? unassignReason : null,
                    "linked_movements": linkedMovementData
                }
            });

            if (!showResults) return;

            const updated = unassignStatusList.data.filter(result => result.success);

            const failedResults = unassignStatusList.data.filter(result => !result.success && result.retry);
            const failedRows = failedResults.map(result => {
                const row = selectedRows.find(row => row.data.getString("movement_id") === result.keys.movement_id);
                if (row) {
                    row.data.set("error", result.message);
                    return row;
                }
                // since we may be unassigning a linked move, it's possible the movement_id being returned doesn't exist in the selectedRows
                else if (row === undefined && hasLinkedId) {
                    const newRow = new ModelRow("", false);
                    newRow.set("movement_id", result.keys.movement_id);
                    newRow.set("error", result.message);
                    return { data: newRow } as TableRow;
                }
            });

            const erroredResults = unassignStatusList.data.filter(result => !result.success && !result.retry);
            const erroredRows = erroredResults.map(result => {
                const row = selectedRows.find(row => row.data.getString("movement_id") === result.keys.movement_id);
                if (row) {
                    const modelRow = row.data.createBasicCopy();
                    modelRow.set("error", result.error);
                    return modelRow;
                }
                else if (row === undefined && hasLinkedId) {
                    const newRow = new ModelRow("", false);
                    newRow.set("error", result.error);
                    return newRow;
                }
            });

            const retryAction = (selectedErrorRows: TableRow[]) => this.unassignSelectedEquipment(selectedErrorRows, unassignReason, showResults);
            OrderMultiSelectErrorModal.showModal(failedRows, erroredRows, updated.length, "Multi-Select Unassign Equipment", "Retry", retryAction, undefined, true);

        } catch (e) {
            Snackbar.showWarningSnackbar(e);
        } finally {
            if (selectedRows.length > 1) {
                selectedRows[0].table.selectedRows = [];
            }
            inProgressSnackbar.dismiss();
        }
    }
}
